/**
 * ⌘K's rows for the selected node's paid verbs — the six moves, the source check
 * (the survey, on the seed), the panel and the conjecture line's three — and the
 * one press they all make.
 *
 * Each row is the key's press spelled out, so it takes the key's path: with the
 * lock free the verb runs now, and under a pass in flight it joins the Up next
 * line behind it or is refused out loud in the line's own sentence (D162). The
 * rows used to vanish for any pass in flight, which sent a reader who had
 * learned the palette to a list with the verb they came for missing and no word
 * why. A row now stands down only where the line itself is shut — a live run, or
 * two presses already up next — as the card's buttons grey out, because the
 * palette's rule is that it never offers what the store would refuse.
 *
 * Pure and outside the component, as `paletteSearch.ts` is, so which rows are
 * offered under the lock is pinned by a test rather than by a render the Node
 * suite cannot make. A row is data; `pressPaletteVerb` is its press.
 */

import { OP_HINT, OP_LABEL, type ReasonNode, type RunVerb } from '../core/types'
import { CONJECTURE_LINE_WORD, upNextShut, type UpNextVerb, type UpNextView } from '../core/upNext'
import type { IconName } from './components/Icon'
import { pressVerb, type PressOutcome } from './upNextPress'

export interface PaletteVerbRow {
  id: string
  verb: UpNextVerb
  title: string
  sub: string
  icon: IconName
}

/** The six, in the card's own order, each wearing its own icon. */
const MOVES: ReadonlyArray<[RunVerb, IconName]> = [
  ['deepen', 'deepen'],
  ['challenge', 'challenge'],
  ['diverge', 'diverge'],
  ['experiment', 'experiment'],
  ['invert', 'invert'],
  ['expand', 'expand'],
]

/**
 * Which search a check press on a node is: the source check (the survey, on the
 * seed), or on a conjecture its prior-art search — the one search a conjecture
 * gets (D281), because a source check on a proposal nobody has made can only
 * read "nothing found" (D288). V and this palette read it, so the key and the
 * row cannot disagree about what a check on a conjecture is.
 */
export const checkPressOf = (n: Pick<ReasonNode, 'kind'>): 'verify' | 'priorArt' =>
  n.kind === 'conjecture' ? 'priorArt' : 'verify'

/**
 * The rows the selection offers now. The moves ask the lock the moves' question
 * and the checks theirs (`upNextShut`): the worked example's unfurl gives way to
 * a move (D122) and a check waits behind it, so both are offered while it
 * unfolds.
 */
export function paletteVerbRows(
  s: Pick<UpNextView, 'busy' | 'interrupt' | 'run' | 'session' | 'upNext'>,
  sel: Pick<ReasonNode, 'parentId' | 'conjecture' | 'kind'>,
): PaletteVerbRow[] {
  const out: PaletteVerbRow[] = []
  // Titled and glossed from the shared tables, not from literals here. This
  // row read "Design a test" while the inspector's button read "Test" — one
  // verb under two names, in the two places a user compares them. Keyed off
  // OP_HINT, which covers exactly the six that belong in front of a node.
  if (!upNextShut(s, 'deepen')) {
    for (const [op, icon] of MOVES)
      out.push({ id: `op-${op}`, verb: op, title: OP_LABEL[op], sub: OP_HINT[op], icon })
  }
  // The two checks on a claim. Grouped with the verbs because that is where the
  // user is looking.
  if (sel.parentId !== null) {
    // Never on a conjecture: its search is the prior-art search, which the
    // palette offers as a row of its own (`checkPressOf`).
    if (checkPressOf(sel) === 'verify' && !upNextShut(s, 'verify'))
      out.push({
        id: 'cmd-verify',
        verb: 'verify',
        title: 'Check this claim against sources',
        sub: 'Searches the live web and reports what it says. Needs a key.',
        icon: 'search',
      })
    if (!upNextShut(s, 'panel'))
      out.push({
        id: 'cmd-panel',
        verb: 'panel',
        title: 'Ask three lenses separately',
        sub: 'Three independent passes, then count where they landed. Needs a key.',
        icon: 'invert',
      })
  } else if (!upNextShut(s, 'verify')) {
    // The seed's own V: the survey of the field, which is a check's pass and
    // price on the question rather than on a claim.
    out.push({
      id: 'cmd-survey',
      verb: 'verify',
      title: 'Survey the field around this question',
      sub: 'Searches the live web for what has been found, tried and disputed. Priced like a source check. Needs a key.',
      icon: 'search',
    })
  }
  // The conjecture line's three, beside the verbs but not among them: they
  // propose and search rather than take apart. They wait on the line as V does
  // (D286); they used to be built behind `!busy` and vanish for any pass in
  // flight, where D167 has a row stand down only when the line is shut.
  if (!upNextShut(s, 'conjecture'))
    out.push({
      id: 'op-conjecture',
      verb: 'conjecture',
      title: CONJECTURE_LINE_WORD.conjecture,
      sub: 'Claims no source the model knows states, each with a prediction that would tell it apart. Needs a key.',
      icon: 'conjecture',
    })
  // A conjecture's own search. Only on a conjecture: it reads what one departs
  // from, its leap and its prediction, and a claim has none.
  if (sel.conjecture && !upNextShut(s, 'priorArt'))
    out.push({
      id: 'op-prior-art',
      verb: 'priorArt',
      title: CONJECTURE_LINE_WORD.priorArt,
      sub: 'A search that assumes this insight has been said before and goes after where. The most it can report is that it did not find it.',
      icon: 'search',
    })
  if (!upNextShut(s, 'sweep'))
    out.push({
      id: 'op-sweep',
      verb: 'sweep',
      title: CONJECTURE_LINE_WORD.sweep,
      sub: 'Where the literature on this is unsettled, each point resting on one returned page and quoting it. Items never become nodes.',
      icon: 'search',
    })
  return out
}

/** A row's press: the key's own, through the one press path. */
export const pressPaletteVerb = (row: Pick<PaletteVerbRow, 'verb'>, nodeId: string): PressOutcome =>
  pressVerb(row.verb, nodeId)
